import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Globe2, Radar, Scale, ShieldAlert, MessageSquareText, Headphones,
  Coins, ArrowRight, Check, TrendingUp, Cpu, Search, Calculator,
} from "lucide-react";
import api from "@/lib/api";
import { useLocale } from "@/context/AppContext";
import { ThemeToggle, LanguageMenu, CurrencyMenu } from "@/components/shared/Controls";
import { Button } from "@/components/ui/button";

const HERO_IMG = "https://images.pexels.com/photos/262353/pexels-photo-262353.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const FEATURES = [
  { icon: Radar, title: "Radar mundial", desc: "Global Scanner AI busca promoções, liquidações e diferenças de preço entre mercados." },
  { icon: Scale, title: "Análise de margem", desc: "Price Analyzer calcula custo total, margem %, ROI e lucro estimado por produto." },
  { icon: ShieldAlert, title: "Controle de risco", desc: "Risk Analyzer classifica cada operação como BAIXO, MÉDIO ou ALTO — nada é sem risco." },
  { icon: MessageSquareText, title: "IA de vendas", desc: "Sales AI cria ofertas, títulos e argumentos adaptados por país, idioma e cultura." },
  { icon: Headphones, title: "Atendimento inteligente", desc: "Customer Service AI responde dúvidas 24h em vários idiomas sem inventar dados." },
  { icon: Coins, title: "Multi-moeda", desc: "Conversão em tempo real (BRL, USD, EUR e mais) com taxa e horário de atualização." },
];

const STEPS = [
  { icon: Search, title: "Radar", desc: "A IA varre mercados e fontes autorizadas." },
  { icon: Cpu, title: "Multi-agente", desc: "Preço, mercado e risco analisam em conjunto." },
  { icon: Calculator, title: "Cálculo de margem", desc: "Custo total, margem e ROI estimados." },
  { icon: TrendingUp, title: "Execução", desc: "Você decide comprar, criar oferta ou descartar." },
];

export default function Landing() {
  const { t } = useLocale();
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    api.get("/plans").then(({ data }) => setPlans(data.plans)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center">
              <Globe2 className="h-5 w-5 text-primary-foreground" />
            </div>
            <span className="font-heading font-bold text-base sm:text-lg tracking-tight whitespace-nowrap">GLOBAL PROFIT AI</span>
          </div>
          <nav className="hidden md:flex items-center gap-6 text-sm text-muted-foreground">
            <a href="#how" className="hover:text-foreground transition-colors">{t("nav_how")}</a>
            <a href="#features" className="hover:text-foreground transition-colors">{t("nav_radar")}</a>
            <a href="#plans" className="hover:text-foreground transition-colors">{t("nav_plans")}</a>
          </nav>
          <div className="flex items-center gap-0.5 sm:gap-1">
            <span className="hidden sm:inline-flex"><CurrencyMenu /></span>
            <LanguageMenu />
            <ThemeToggle />
            <Button asChild variant="ghost" size="sm" className="hidden sm:inline-flex" data-testid="nav-login">
              <Link to="/login">{t("nav_login")}</Link>
            </Button>
            <Button asChild size="sm" data-testid="nav-start">
              <Link to="/register"><span className="sm:hidden">Começar</span><span className="hidden sm:inline">{t("cta_start")}</span></Link>
            </Button>
          </div>
        </div>
      </header>

      {/* Intro banner */}
      <div className="border-b border-border bg-secondary/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <p className="text-sm sm:text-base text-muted-foreground text-center" data-testid="intro-message">
            <span className="font-semibold text-foreground">Global Profit AI</span> é uma plataforma de inteligência comercial desenvolvida para ajudar empreendedores a pesquisar oportunidades de compra e venda, analisar custos, estimar margens e tomar decisões com base em dados.
          </p>
        </div>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden grid-glow">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-center relative z-10">
          <div className="space-y-7">
            <span className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-mono uppercase tracking-widest text-primary">
              Global Trade Intelligence 2026
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-none">
              {t("hero_title")}
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground max-w-xl">{t("hero_sub")}</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button asChild size="lg" className="group" data-testid="hero-start">
                <Link to="/register">{t("cta_start")} <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-0.5 transition-transform" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" data-testid="hero-explore">
                <Link to="/login">{t("cta_explore")}</Link>
              </Button>
            </div>
            <p className="text-xs text-muted-foreground font-mono">Começa conservador: limite R$50 / US$20 por produto. Você escala quando decidir.</p>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }}
            className="relative rounded-2xl overflow-hidden border border-border shadow-2xl">
            <img src={HERO_IMG} alt="Comércio global" className="w-full h-[380px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
            <div className="absolute bottom-4 left-4 right-4 flex gap-3">
              {[["Margem", "34.8%"], ["ROI", "+18.2%"], ["Risco", "MÉDIO"]].map(([k, v]) => (
                <div key={k} className="flex-1 rounded-xl bg-card/90 backdrop-blur border border-border p-3">
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">{k}</p>
                  <p className="font-heading font-bold text-lg">{v}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight mb-12 text-center">Como funciona</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {STEPS.map((s, i) => (
            <div key={s.title} className="relative rounded-xl border border-border bg-card p-6">
              <div className="h-11 w-11 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                <s.icon className="h-5 w-5 text-primary" />
              </div>
              <p className="font-mono text-xs text-primary mb-1">0{i + 1}</p>
              <h3 className="font-heading font-semibold text-lg mb-1">{s.title}</h3>
              <p className="text-sm text-muted-foreground">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section id="features" className="border-y border-border bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-2xl mb-12">
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight mb-3">Um sistema, dez agentes de IA</h2>
            <p className="text-muted-foreground">Coordenados pelo Orchestrator AI para transformar dados em oportunidades — sempre como estimativas transparentes.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f) => (
              <div key={f.title} className="rounded-xl border border-border bg-card p-6 hover:border-primary/40 transition-colors">
                <div className="h-11 w-11 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <f.icon className="h-5 w-5 text-primary" />
                </div>
                <h3 className="font-heading font-semibold text-lg mb-1">{f.title}</h3>
                <p className="text-sm text-muted-foreground">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Plans */}
      <section id="plans" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight mb-3 text-center">Planos</h2>
        <p className="text-muted-foreground text-center mb-12">Comece grátis. Escale conforme suas oportunidades crescem.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((p) => (
            <div key={p.lookup_key} className={`rounded-xl border p-6 flex flex-col ${p.name === "PRO" ? "border-primary ring-1 ring-primary/40" : "border-border"} bg-card`}>
              {p.name === "PRO" && <span className="text-xs font-mono uppercase tracking-widest text-primary mb-2">Mais popular</span>}
              <h3 className="font-heading font-bold text-xl">{p.name}</h3>
              <p className="font-heading text-3xl font-bold mt-2">${p.price}<span className="text-sm text-muted-foreground font-normal">/mês</span></p>
              <ul className="mt-5 space-y-2 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <Button asChild variant={p.name === "PRO" ? "default" : "outline"} className="mt-6 w-full" data-testid={`plan-${p.lookup_key}`}>
                <Link to="/register">{p.price === 0 ? t("cta_start") : "Assinar"}</Link>
              </Button>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 flex flex-col sm:flex-row justify-between gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <Globe2 className="h-4 w-4 text-primary" />
            <span className="font-heading font-semibold text-foreground">GLOBAL PROFIT AI</span>
          </div>
          <p className="font-mono text-xs max-w-md">Ferramenta de análise e automação. Resultados são estimativas baseadas nos dados disponíveis. Não há garantia de lucro, vendas ou ausência de perdas.</p>
        </div>
      </footer>
    </div>
  );
}
